import { useCallback, useRef } from 'react'

type SoundType = 'click' | 'success' | 'error' | 'navigation'

const useSound = (type: SoundType = 'click') => {
  const audioContextRef = useRef<AudioContext | null>(null)
  const gainNodeRef = useRef<GainNode | null>(null)

  const playSound = useCallback(() => {
    // Check if user has disabled sounds
    const soundsEnabled = localStorage.getItem('noe_sounds_enabled') !== 'false'
    if (!soundsEnabled) return

    try {
      // Initialize audio context on first play
      if (!audioContextRef.current) {
        const AudioContextClass = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioContextClass) {
          return; // Audio context not supported
        }
        audioContextRef.current = new AudioContextClass();
        gainNodeRef.current = audioContextRef.current.createGain();
        gainNodeRef.current.connect(audioContextRef.current.destination);
      }

      const audioContext = audioContextRef.current
      const gainNode = gainNodeRef.current!

      // Resume context if suspended (browser policy)
      if (audioContext.state === 'suspended') {
        audioContext.resume()
      }

      // Configure sound parameters based on type
      let frequency = 800
      let duration = 0.1
      let volume = 0.1

      switch (type) {
        case 'click':
          frequency = 600
          duration = 0.05
          volume = 0.08
          break
        case 'success':
          frequency = 1000
          duration = 0.2
          volume = 0.1
          break
        case 'error':
          frequency = 300
          duration = 0.3
          volume = 0.1
          break
        case 'navigation':
          frequency = 800
          duration = 0.15
          volume = 0.09
          break
      }

      // Create oscillator
      const oscillator = audioContext.createOscillator()
      oscillator.connect(gainNode)
      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)

      // Configure volume envelope
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.01)
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration)

      // Play and cleanup
      oscillator.start()
      oscillator.stop(audioContext.currentTime + duration)

      // Cleanup oscillator after it's done
      oscillator.onended = () => {
        oscillator.disconnect()
      }
    } catch (error) {
      console.warn('Failed to play sound:', error)
      // Silently fail if audio context is not supported
    }
  }, [type])

  return playSound
}

export default useSound

// Utility function to toggle sounds on/off
export const toggleSounds = (enabled: boolean) => {
  localStorage.setItem('noe_sounds_enabled', enabled.toString())
}

// Utility function to check if sounds are enabled
export const areSoundsEnabled = (): boolean => {
  return localStorage.getItem('noe_sounds_enabled') !== 'false'
}